/**
 * Copyright 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { ApolloError } from "@apollo/client";
import { Spacer, theme } from "@nordcloud/gnui";
import { BreadcrumbsBox, UniversalWrap } from "~/components";
import {
  DefaultEc2Header,
  useDefaultEc2HeaderData,
} from "../components/DefaultEc2Components";
import { Ec2InstanceApplications, EstateRecord } from "../Ec2Instance";
import { useEstateDetails } from "../hooks";
import { ApplicationProps, Costs } from "../types";
import { getResourceVisabilityDates } from "../utils";
import { Ec2SecurityGroupDetails } from "./Ec2SecurityGroupDetails";
import { Ec2SecurityGroupTabs } from "./Ec2SecurityGroupTabs";
import { useEc2SecurityGroupDetails } from "./hooks/useEc2SecurityGroupDetails";

type Props = {
  header: EstateRecord;
  data: ReturnType<typeof useEstateDetails>["data"];
  applications: ApplicationProps;
  loading: boolean;
  error: ApolloError | undefined;
  costs: Costs;
};

export function Ec2SecurityGroupPage({
  header,
  data,
  applications,
  loading,
  error,
  costs,
}: Props) {
  const { metadata, breadcrumbLabels } = data;

  const { name } = header;
  const { estateRecord } = data;
  const headerData = useDefaultEc2HeaderData(header, costs);
  const resourceLifecycleDates = getResourceVisabilityDates(estateRecord);
  const details = useEc2SecurityGroupDetails(metadata, resourceLifecycleDates);

  return (
    <UniversalWrap loaderProps={{ loading }} errorProps={{ error }}>
      <BreadcrumbsBox title={name} labels={breadcrumbLabels} />
      <DefaultEc2Header {...headerData} />
      <Spacer height={theme.spacing.spacing04} />
      <Ec2SecurityGroupDetails {...details} />
      <Spacer height={theme.spacing.spacing04} />
      <Ec2InstanceApplications {...applications} />
      <Spacer height={theme.spacing.spacing04} />
      <Ec2SecurityGroupTabs data={data} />
    </UniversalWrap>
  );
}
